import axiosInstance from '../../config/axiosInstance';
import { PermissionV2 } from '../../types/role';
import {
  AddOrganizationMemberByUserPayload,
  AddOrganizationMemberPayload,
  OrganizationMemberV2,
  OrganizationRolePayload,
  OrganizationRoleV2,
  OrganizationTeamSummaryV2,
  TeamScope,
} from '../../types/organizationTeam';

/**
 * An organization's team. `my` is the caller's own organization (owner only);
 * `org` is any organization (needs organizations.manage).
 */
export class OrganizationTeamService {
  basePath(scope: TeamScope): string {
    return scope.kind === 'my' ? '/organizations/my' : `/organizations/${scope.orgId}/team`;
  }

  async summary(orgId: string): Promise<OrganizationTeamSummaryV2> {
    return axiosInstance.get(`/organizations/${orgId}/team`);
  }

  async listPermissions(scope: TeamScope): Promise<PermissionV2[]> {
    return axiosInstance.get(`${this.basePath(scope)}/permissions`);
  }

  async listRoles(scope: TeamScope): Promise<OrganizationRoleV2[]> {
    return axiosInstance.get(`${this.basePath(scope)}/roles`);
  }

  async createRole(scope: TeamScope, payload: OrganizationRolePayload): Promise<OrganizationRoleV2> {
    return axiosInstance.post(`${this.basePath(scope)}/roles`, payload);
  }

  async updateRole(
    scope: TeamScope,
    id: string,
    payload: Partial<OrganizationRolePayload>,
  ): Promise<OrganizationRoleV2> {
    return axiosInstance.patch(`${this.basePath(scope)}/roles/${id}`, payload);
  }

  async deleteRole(scope: TeamScope, id: string): Promise<{ deleted: true }> {
    return axiosInstance.delete(`${this.basePath(scope)}/roles/${id}`);
  }

  async listMembers(scope: TeamScope): Promise<OrganizationMemberV2[]> {
    return axiosInstance.get(`${this.basePath(scope)}/members`);
  }

  async addMember(
    scope: TeamScope,
    payload: AddOrganizationMemberPayload | AddOrganizationMemberByUserPayload,
  ): Promise<OrganizationMemberV2> {
    return axiosInstance.post(`${this.basePath(scope)}/members`, payload);
  }

  async setMemberRole(scope: TeamScope, userId: string, roleId: string): Promise<OrganizationMemberV2> {
    return axiosInstance.patch(`${this.basePath(scope)}/members/${userId}/role`, { roleId });
  }

  async removeMember(scope: TeamScope, userId: string): Promise<{ removed: true }> {
    return axiosInstance.delete(`${this.basePath(scope)}/members/${userId}`);
  }
}

export const organizationTeamService = new OrganizationTeamService();
